<template>
  <banners
    :sticky-top="true"
    :narrow="true"
    :is-ultra="true"
  >
    <div class="flex w-full">
      <div
        :class="{
          'relative w-[900px] overflow-scroll': isDesktop,
          'w-full': !isDesktop,
          'pb-14': !isLoadBoard,
          'h-[calc(100vh-80px)]': showBanner,
          'h-[calc(100vh-52px)]': !showBanner,
        }"
      >
        <div class="mx-4 mb-3 mt-6 bg-white">
          <div class="container-max m-auto">
            <div class="flex items-center justify-between">
              <router-link
                :to="backUrl"
                title="Back to Loads"
                class="-ml-2 flex max-w-[66%] items-center"
              >
                <svg
                  class="min-w-7"
                  width="32"
                  height="33"
                  viewBox="0 0 32 33"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M19 10.8906L13 16.8906L19 22.8906"
                    stroke="#313545"
                    stroke-width="2"
                  />
                </svg>
                <p
                  v-if="isLoadBoard"
                  class="overflow-hidden text-ellipsis whitespace-nowrap text-2xl"
                >
                  {{ isDesktop ? 'Load ' : ''
                  }}{{ load?.external_order_or_load_number }}
                </p>
                <p
                  v-else
                  class="overflow-hidden text-ellipsis whitespace-nowrap text-2xl"
                >
                  {{ isDesktop ? 'Load ' : '' }}{{ load?.shipment_identifier }}
                </p>
              </router-link>

              <div v-if="!isLoadBoard && !isLoading">
                <p
                  class="rounded-full px-2 text-sm capitalize"
                  :class="{
                    'bg-yellow-100 text-yellow-700': load.status === 'covered',
                    'bg-blue-100 text-blue-500': load.status === 'progress',
                    'bg-green-100 text-green-800':
                      load.status === 'assumed_delivered',
                    'bg-red-150 text-red-500':
                      load.status === 'assign_vehicle' ||
                      load.status === 'available',
                  }"
                >
                  {{ formatLoadStatus(load.status) }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="isLoading"
          class="mt-6"
        >
          <img
            class="loading m-auto h-12 w-12"
            src="@/assets/loading-v2-yellow.svg"
            alt="loading spinner"
          />
        </div>

        <div v-else>
          <LoadDetailMap
            v-if="!loadDetailsLoading && !isDesktop"
            :load="load"
          ></LoadDetailMap>

          <div class="bg-light-gray px-4 pb-8 lg:bg-white lg:pb-0">
            <div class="container-max m-auto flex min-h-[80vh] flex-col gap-4">
              <div
                v-if="isLoadBoard"
                :class="{
                  'z-10 -mt-8 lg:mt-4': !isDesktop,
                }"
              >
                <div
                  v-if="placed_offer?.id && placed_offer?.status !== 'rejected'"
                  class="mb-3 flex gap-2 rounded-md border border-ocean-400 bg-ocean-100 p-3"
                >
                  <img
                    class="flex h-6 w-6 items-center text-2xs"
                    src="@/assets/alert-circle.svg"
                    alt="alert icon"
                  />

                  <div class="flex flex-col items-start">
                    <p class="text-lg">
                      {{ offer_status }}
                    </p>

                    <p class="text-left font-normal text-steel-900">
                      {{ offer_text }}
                    </p>

                    <div
                      v-if="
                        !manage_offer_enabled &&
                        placed_offer &&
                        placed_offer?.status !== 'accepted'
                      "
                      class="flex gap-12 pt-2"
                    >
                      <img
                        v-if="offer_loading"
                        class="loading m-auto"
                        src="@/assets/loading-v2-yellow.svg"
                        alt="loading spinner"
                      />
                      <button
                        v-else
                        class="text-ocean-800 hover:text-ocean-600"
                        @click="handleWithdrawOffer"
                      >
                        Withdraw Offer
                      </button>
                    </div>
                  </div>
                </div>

                <div
                  :class="{
                    'rounded-xl border border-light-gray bg-white px-4 py-2 shadow-md lg:mt-4':
                      !isDesktop,
                  }"
                >
                  <div
                    :class="{
                      'rounded-lg border border-light-gray px-3': isDesktop,
                    }"
                  >
                    <div
                      v-if="isDesktop"
                      class="mt-4 flex flex-col items-start gap-2"
                    >
                      <span
                        v-if="lane_prediction && loadRateText"
                        class="rounded px-2 py-1 text-xs"
                        :class="loadRateClass"
                      >
                        {{ loadRateText }}
                      </span>
                      <div class="flex items-end gap-1">
                        <h3 class="text-3xl font-semibold">
                          {{ formatPrice(load.price) }}
                        </h3>
                        <p
                          v-if="ratePerMile && load?.price"
                          class="mb-[4px] w-max text-2xs text-neutral-subtlest"
                        >
                          {{ ratePerMile }}/mi
                        </p>
                      </div>
                    </div>

                    <div
                      v-if="!isDesktop"
                      class="flex justify-between"
                    >
                      <div class="flex flex-col items-start">
                        <span
                          v-if="loadRateText"
                          class="rounded px-2 py-1 text-xs"
                          :class="loadRateClass"
                        >
                          {{ loadRateText }}
                        </span>

                        <div class="flex items-end gap-2 pt-2">
                          <h3 class="text-3xl font-semibold">
                            {{ formatPrice(load.price) }}
                          </h3>
                          <p
                            v-if="ratePerMile && load?.price"
                            class="-mt-2 text-1xs text-neutral-subtlest"
                          >
                            {{ ratePerMile }}/mi
                          </p>
                        </div>
                      </div>
                    </div>

                    <div class="my-4 flex items-center justify-between">
                      <div
                        v-if="billableMiles"
                        class="flex gap-1"
                      >
                        <img
                          src="@/assets/loads/map.svg"
                          alt="map icon"
                        />
                        <p class="text-xs sm:text-sm">
                          {{ billableMiles }} mi Trip
                        </p>
                      </div>

                      <div class="flex items-center gap-1">
                        <img
                          class="h-5 w-5"
                          src="@/assets/loads/destination-marker.svg"
                          alt="destination icon"
                        />
                        <p class="text-xs sm:text-sm">
                          {{ numberOfStops }}
                        </p>
                      </div>

                      <div class="flex items-center gap-1 sm:gap-2">
                        <img
                          class="h-4 w-4"
                          src="@/assets/phone-arrow.svg"
                          alt="phone icon"
                        />
                        <a
                          v-if="formattedPhone"
                          class="text-xs sm:text-sm"
                          :href="`tel:+${load.broker_phone_number}${load.broker_phone_extension ? `,${load.broker_phone_extension}` : ''}`"
                        >
                          {{ formattedPhone }}
                        </a>
                        <p v-else>-</p>
                      </div>
                    </div>

                    <div v-if="lane_prediction">
                      <img
                        class="mb-4 mt-2 text-2xs"
                        src="@/assets/loads/triumph-intelligence.svg"
                        alt="triumph logo"
                      />

                      <div class="mb-8 mt-1">
                        <div class="mb-2 flex justify-between">
                          <div>
                            <p
                              class="text-sm font-normal text-neutral-subtlest"
                            >
                              Low:
                              {{
                                formatPrice(lane_prediction?.low_buy_rate)
                              }}/mi
                            </p>
                          </div>
                          <div>
                            <p
                              class="text-sm font-normal text-neutral-subtlest"
                            >
                              High:
                              {{
                                formatPrice(lane_prediction?.high_buy_rate)
                              }}/mi
                            </p>
                          </div>
                        </div>

                        <div class="relative flex gap-[2px]">
                          <span
                            v-if="isBelowMarket"
                            class="flex h-4 w-1/12 rounded-l-md bg-red-500"
                          ></span>
                          <span
                            class="flex h-4 w-2/12 bg-[#2AC6E8]"
                            :class="{
                              'rounded-l-md': !isBelowMarket,
                            }"
                          ></span>
                          <span
                            class="flex h-4 bg-[#18A5CB]"
                            :class="
                              isBelowMarket || isAboveMarket
                                ? 'w-7/12'
                                : 'w-8/12'
                            "
                          ></span>
                          <span
                            class="flex h-4 w-2/12 bg-[#136782]"
                            :class="{
                              'rounded-r-md': !isAboveMarket,
                            }"
                          ></span>
                          <span
                            v-if="isAboveMarket"
                            class="flex h-4 w-1/12 rounded-r-md bg-green-900"
                          ></span>

                          <div
                            v-if="load?.rate_per_mile"
                            class="absolute -bottom-4 flex h-5 w-5 flex-col items-center"
                            :style="`left: calc(${targetRatePercentage}% - 10px)`"
                          >
                            <img
                              class="h-5 w-5"
                              src="@/assets/loads/orange-arrow.svg"
                              alt="arrow"
                            />
                          </div>
                          <p
                            v-if="load?.rate_per_mile"
                            class="absolute -bottom-8 min-w-max text-sm leading-none"
                            :style="`left: calc(${targetRatePercentage}% - 24px)`"
                            :class="{
                              'ml-4': targetRatePercentage < 10,
                              '-ml-8': targetRatePercentage >= 95,
                              '-ml-2':
                                targetRatePercentage >= 85 &&
                                targetRatePercentage < 95,
                            }"
                          >
                            {{ ratePerMile }}/mi
                          </p>
                        </div>
                      </div>

                      <div class="mb-6 mt-10 flex flex-col items-start gap-2">
                        <div
                          v-if="quickPayRate"
                          class="flex items-center rounded-lg bg-light-gray px-3 py-2 text-sm font-normal text-neutral-subtle"
                        >
                          <img
                            class="mr-2 h-5 w-5"
                            src="@/assets/loads/triumph-logo-blue.svg"
                            alt="triumph logo"
                          />
                          QuickPay - {{ quickPayRate }}%
                        </div>
                      </div>
                    </div>
                    <div
                      v-else
                      class="pb-4 pt-2"
                    >
                      <p class="text-sm text-steel-700">
                        Market Rate Unavailable
                      </p>
                    </div>
                  </div>

                  <div
                    v-if="isLoadBoard && isDesktop"
                    class="mt-4 rounded-lg border border-light-gray p-3"
                  >
                    <p class="pb-2 text-left text-xl font-medium">Details</p>

                    <div class="flex flex-col gap-2 pb-2">
                      <div
                        v-if="load?.last_posted_at"
                        class="flex justify-between"
                      >
                        <p class="text-steel-700">Post Age</p>
                        <p class="text-steel-900">
                          {{ fromNowFirstUnit(load.last_posted_at) }}
                        </p>
                      </div>

                      <div class="flex justify-between">
                        <p class="text-steel-700">Weight</p>
                        <p class="text-steel-900">
                          {{ loadWeight }}
                        </p>
                      </div>

                      <div
                        v-if="canShowLoadComments"
                        class="flex justify-between"
                      >
                        <p class="text-steel-700">Commodity</p>

                        <Popper
                          v-if="loadCommodity?.length > MAX_COMMODITY_LENGTH"
                          placement="bottom-end"
                          :hover="true"
                          :arrow="true"
                          offset-distance="6"
                          class="col-span-4 !m-0 !flex h-full items-center !border-0 pb-2"
                        >
                          <p class="text-end text-steel-900">
                            {{ loadCommodity }}
                          </p>

                          <template #content>
                            <div
                              class="max-w-[350px] rounded-md bg-dark-gray p-3 text-left text-xs text-steel-200"
                            >
                              <p>
                                {{ load?.posted_commodity_description }}
                              </p>
                            </div>
                          </template>
                        </Popper>
                        <p
                          v-else
                          class="text-end text-steel-900"
                        >
                          {{ loadCommodity }}
                        </p>
                      </div>

                      <div
                        v-if="
                          load?.is_hazmat_required || load?.is_teams_required
                        "
                        class="flex justify-between"
                      >
                        <p class="text-steel-700">Type</p>

                        <div
                          class="flex max-w-[300px] flex-wrap justify-end gap-2"
                        >
                          <span
                            v-if="load?.is_hazmat_required"
                            class="text-neutral-bold rounded-md bg-steel-100 px-2 py-[2px] text-sm text-xs capitalize"
                          >
                            Hazmat
                          </span>
                          <span
                            v-if="load?.is_teams_required"
                            class="text-neutral-bold rounded-md bg-steel-100 px-2 py-[2px] text-sm text-xs capitalize"
                          >
                            Teams
                          </span>
                        </div>
                      </div>

                      <div
                        v-if="loadComments"
                        class="flex flex-col items-start gap-2"
                      >
                        <p class="text-steel-700">Special Instructions</p>
                        <p
                          class="max-w-full break-words text-left text-steel-900"
                        >
                          {{ loadComments }}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="isLoadBoard && isDesktop"
                    class="mt-4 flex flex-col"
                  >
                    <div class="rounded-lg border border-light-gray p-3">
                      <p class="mb-2 text-left text-xl font-medium">
                        Equipment
                      </p>

                      <div class="mb-2 flex flex-col gap-2 font-normal">
                        <div
                          class="col-span-3 flex items-center justify-between"
                        >
                          <p class="text-neutral-subtlest">Truck Type</p>

                          <p class="text-steel-900">
                            {{
                              formatEquipmentType(
                                load?.posted_equipment_types
                              ) || '-'
                            }}
                          </p>
                        </div>

                        <div
                          v-if="
                            load?.posted_equipment_types &&
                            load?.posted_equipment_types !== undefined &&
                            load?.posted_equipment_types.includes('reefer') &&
                            (load?.temperature_min || load?.temperature_max)
                          "
                          class="col-span-3 flex items-center justify-between"
                        >
                          <p class="text-neutral-subtlest">Temperature Range</p>
                          <p class="text-steel-900">
                            {{ load?.temperature_min }}&deg;{{
                              load.temperature_unit == 'fahrenheit' ? 'F' : 'C'
                            }}
                            - {{ load?.temperature_max }}&deg;{{
                              load.temperature_unit == 'fahrenheit' ? 'F' : 'C'
                            }}
                          </p>
                        </div>

                        <div
                          v-if="load?.trailer_length"
                          class="col-span-3 flex items-center justify-between"
                        >
                          <p class="text-neutral-subtlest">Length</p>

                          <p class="text-steel-900">
                            {{ trailerLength }}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div class="mt-4">
                      <div
                        v-for="(stop, stopIndex) in load.stops"
                        :key="stop.id"
                        class="border border-light-gray p-3"
                        :class="{
                          'rounded-t-lg': stopIndex === 0,
                          'rounded-b-lg': stopIndex === load.stops?.length - 1,
                        }"
                      >
                        <p class="mb-2 text-left text-xl font-medium">
                          Stop {{ stopIndex + 1 }}
                        </p>

                        <div class="flex flex-col gap-2 font-normal">
                          <div
                            class="col-span-3 flex items-center justify-between"
                          >
                            <p class="text-neutral-subtlest">Type</p>
                            <p class="text-steel-900">
                              {{
                                stop.trailer_operation_type == 'loading'
                                  ? 'Loading'
                                  : 'Unloading'
                              }}
                            </p>
                          </div>

                          <div
                            class="col-span-3 flex items-center justify-between"
                          >
                            <p class="text-neutral-subtlest">Location</p>

                            <p class="text-steel-900">
                              {{ formattedCityStateAddress(stop) }}
                            </p>
                          </div>

                          <div
                            class="col-span-3 flex items-center justify-between"
                          >
                            <p class="text-neutral-subtlest">Appointment</p>
                            <p class="text-steel-900">
                              {{ formatStopWindow(stop) }}
                            </p>
                          </div>

                          <div
                            v-if="stop?.comments"
                            class="flex flex-col items-start gap-2"
                          >
                            <p class="text-neutral-subtlest">Comments</p>
                            <p
                              class="max-w-full break-words text-left text-steel-900"
                            >
                              {{ limitCharacter(stop?.comments) }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-else
                class="z-10 -mt-8 rounded-xl border border-light-gray bg-white p-4 shadow-md lg:mt-4"
              >
                <router-link
                  v-if="brokerLogo && broker?.carrier_id"
                  :to="{
                    name: 'Carrier Brokers Show Page',
                    params: { id: broker?.carrier_id },
                  }"
                  target="_blank"
                  class="flex items-center justify-center gap-2"
                >
                  <img
                    class="flex h-8 items-center text-2xs"
                    :src="brokerLogo"
                    alt="broker logo"
                  />
                </router-link>
                <div
                  v-else-if="brokerLogo"
                  class="flex items-center justify-center gap-2"
                >
                  <img
                    class="flex h-8 items-center text-2xs"
                    :src="brokerLogo"
                    alt="broker logo"
                  />
                </div>

                <div class="mt-4 flex items-center justify-around">
                  <div
                    v-if="billableMiles"
                    class="flex gap-1"
                  >
                    <img
                      src="@/assets/loads/map.svg"
                      alt="map icon"
                    />
                    <p class="text-xs">{{ billableMiles }}mi Trip</p>
                  </div>
                  <div class="flex items-center gap-1">
                    <img
                      class="h-5 w-5"
                      src="@/assets/loads/destination-marker.svg"
                      alt="destination icon"
                    />
                    <p class="text-xs">
                      {{ numberOfStops }}
                    </p>
                  </div>
                </div>
              </div>

              <div
                v-if="isLoadBoard && !isDesktop"
                :class="{
                  'flex flex-col gap-1.5 rounded-xl border border-light-gray bg-white py-2 shadow-md lg:mt-4':
                    !isDesktop,
                }"
              >
                <p
                  class="border-gray border-b pb-2 pl-4 text-left text-xl font-medium"
                >
                  Details
                </p>

                <div class="flex flex-col gap-1.5 px-4 py-2">
                  <div
                    v-if="load.last_posted_at"
                    class="flex justify-between"
                  >
                    <p class="text-neutral-subtlest">Post Age</p>
                    <p class="text-steel-900">
                      {{ fromNowFirstUnit(load.last_posted_at) }}
                    </p>
                  </div>

                  <div class="flex justify-between">
                    <p class="text-neutral-subtlest">Weight</p>
                    <p class="text-steel-900">
                      {{ loadWeight }}
                    </p>
                  </div>

                  <div
                    v-if="load?.is_hazmat_required || load?.is_teams_required"
                    class="flex justify-between"
                  >
                    <p class="text-neutral-subtlest">Type</p>

                    <div class="flex max-w-[300px] flex-wrap justify-end gap-2">
                      <span
                        v-if="load?.is_hazmat_required"
                        class="text-neutral-bold rounded-md bg-steel-100 px-2 py-[2px] text-sm text-xs capitalize"
                      >
                        Hazmat
                      </span>
                      <span
                        v-if="load?.is_teams_required"
                        class="text-neutral-bold rounded-md bg-steel-100 px-2 py-[2px] text-sm text-xs capitalize"
                      >
                        Teams
                      </span>
                    </div>
                  </div>

                  <div
                    v-if="loadComments"
                    class="flex flex-col items-start gap-2"
                  >
                    <p class="text-neutral-subtlest">Special Instructions</p>
                    <p class="max-w-full break-words text-left text-steel-900">
                      {{ loadComments }}
                    </p>
                  </div>
                </div>
              </div>

              <div
                v-if="!isLoadBoard && isDesktop"
                class="mt-4"
              >
                <div
                  v-for="(stop, stopIndex) in load.stops"
                  :key="stop.id"
                  class="border border-light-gray p-3"
                  :class="{
                    'rounded-t-lg': stopIndex === 0,
                    'rounded-b-lg': stopIndex === load.stops?.length - 1,
                  }"
                >
                  <p class="mb-2 text-left text-xl font-medium">
                    Stop {{ stopIndex + 1 }}
                  </p>

                  <div class="flex flex-col gap-2 font-normal">
                    <div class="col-span-3 flex items-center justify-between">
                      <p class="text-neutral-subtlest">Type</p>

                      <p
                        v-if="!stopIndex"
                        class="text-steel-900"
                      >
                        Loading
                      </p>
                      <p
                        v-else
                        class="text-steel-900"
                      >
                        Unloading
                      </p>
                    </div>

                    <div class="col-span-3 flex items-center justify-between">
                      <p class="text-neutral-subtlest">Location</p>

                      <p class="text-steel-900">
                        {{ formattedCityStateAddress(stop) }}
                      </p>
                    </div>

                    <div class="col-span-3 flex items-center justify-between">
                      <p class="text-neutral-subtlest">Appointment</p>

                      <p class="text-steel-900">
                        {{ formattedTime(stop) }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <CollapsibleLoadDetails
                v-if="
                  !isLoadBoard &&
                  load?.assignment_eligible &&
                  !wasAssignedVehicleUpdated
                "
                title="Equipment"
              >
                <div
                  class="flex h-[180px] flex-col justify-center gap-4 border-t border-light-gray p-3 pb-1 font-normal"
                >
                  <p
                    v-if="!assignedVehicle?.id && pendingVehicle?.id"
                    class="rounded-md bg-orange-100 px-2 py-1 text-sm font-normal text-orange-700"
                  >
                    Status: Waiting for driver to activate Highway account.
                  </p>
                  <div
                    v-if="assignedVehicle?.id || pendingVehicle?.id"
                    class="flex flex-col gap-1"
                  >
                    <div class="flex justify-between">
                      <p class="text-gray-600 sm:font-semibold">Truck Number</p>
                      <p
                        :class="{
                          'text-orange-700':
                            !assignedVehicle?.id && pendingVehicle?.id,
                        }"
                      >
                        {{
                          assignedVehicle?.truck_number ||
                          pendingVehicle?.truck_number ||
                          '-'
                        }}
                      </p>
                    </div>

                    <div class="flex justify-between">
                      <p class="text-gray-600 sm:font-semibold">VIN</p>
                      <div>
                        <p
                          :class="{
                            'text-orange-700':
                              !assignedVehicle?.id && pendingVehicle?.id,
                          }"
                        >
                          {{
                            assignedVehicle?.vin || pendingVehicle?.vin || '-'
                          }}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div class="mb-2 flex h-fit flex-col items-center gap-2">
                    <img
                      v-if="!pendingVehicle?.id"
                      src="@/assets/alert-square.svg"
                      alt="alert icon"
                    />
                    <p v-if="assignedVehicle?.id">
                      Please confirm vehicle assignment for this load.
                    </p>
                    <p v-else-if="!pendingVehicle?.id">
                      Please assign a vehicle to this load.
                    </p>

                    <ButtonComponent
                      :variant="
                        assignedVehicle?.id
                          ? 'secondary'
                          : pendingVehicle?.id
                            ? 'default'
                            : 'primary'
                      "
                      @click="showAssignVehicleModal = true"
                    >
                      {{
                        `${assignedVehicle?.id ? 'Confirm' : pendingVehicle?.id ? 'Change' : 'Assign'} Vehicle`
                      }}
                    </ButtonComponent>
                  </div>
                </div>
              </CollapsibleLoadDetails>
              <CollapsibleLoadDetails
                v-if="
                  !isLoadBoard &&
                  (!load?.assignment_eligible || wasAssignedVehicleUpdated)
                "
                title="Equipment"
                :class="{
                  'text-orange-700': !assignedVehicle?.id && pendingVehicle?.id,
                }"
              >
                <div
                  class="flex h-[100px] flex-col justify-center gap-2 border-t border-light-gray p-3 pb-1 font-normal"
                >
                  <p
                    v-if="!assignedVehicle?.id && pendingVehicle?.id"
                    class="rounded-md bg-orange-100 px-2 py-1 text-sm text-orange-700"
                  >
                    Pending: Waiting for driver to activate Highway account.
                  </p>
                  <div
                    v-if="assignedVehicle?.id || pendingVehicle?.id"
                    class="flex flex-col gap-1"
                  >
                    <div class="flex justify-between">
                      <p class="text-gray-600 sm:font-semibold">Truck Number</p>
                      <p>
                        {{
                          assignedVehicle?.truck_number ||
                          pendingVehicle?.truck_number ||
                          '-'
                        }}
                      </p>
                    </div>

                    <div class="flex justify-between">
                      <p class="text-gray-600 sm:font-semibold">VIN</p>
                      <div>
                        <p>
                          {{
                            assignedVehicle?.vin || pendingVehicle?.vin || '-'
                          }}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p v-else>No equipment listed for this load.</p>
                </div>
              </CollapsibleLoadDetails>

              <div v-if="!isDesktop">
                <CollapsibleLoadDetails
                  v-for="(stop, stopIndex) in load.stops"
                  :key="stop.id"
                  :title="`Stop ${stopIndex + 1}`"
                  :badge-text="
                    isLoadBoard ? '' : stopStatus(load.stops, stopIndex)?.status
                  "
                  :badge-color="
                    isLoadBoard ? '' : stopStatus(load.stops, stopIndex)?.color
                  "
                  :class-name="{
                    'mb-4': stopIndex !== load.stops?.length - 1,
                  }"
                >
                  <div
                    class="flex flex-col gap-2 border-t border-light-gray p-3 font-normal"
                  >
                    <div class="flex justify-between">
                      <p class="text-neutral-subtlest sm:font-semibold">Type</p>
                      <p v-if="!stopIndex">Loading</p>
                      <p v-else>Unloading</p>
                    </div>

                    <div class="flex justify-between">
                      <p class="text-neutral-subtlest sm:font-semibold">
                        Address
                      </p>
                      <div class="flex flex-col items-end">
                        <p v-if="!isLoadBoard">
                          {{ formattedStreetAddress(stop) }}
                        </p>
                        <p>{{ formattedCityStateAddress(stop) }}</p>
                      </div>
                    </div>

                    <div class="flex justify-between">
                      <p class="text-neutral-subtlest sm:font-semibold">
                        Appointment
                      </p>
                      <p class="text-steel-900">
                        {{ formatStopWindow(stop) }}
                      </p>
                    </div>
                  </div>
                </CollapsibleLoadDetails>
              </div>

              <CollapsibleLoadDetails
                v-if="!isLoadBoard"
                title="Broker"
                :class-name="!isDesktop ? 'mb-24' : ''"
              >
                <div
                  class="flex flex-col gap-2 border-t border-light-gray p-3 font-normal"
                >
                  <div class="flex justify-between">
                    <img
                      v-if="brokerLogo"
                      class="flex h-6 items-center text-2xs"
                      :src="brokerLogo"
                      alt="broker logo"
                    />
                  </div>
                  <div
                    v-if="load.broker?.legal_name"
                    class="flex items-center justify-between"
                  >
                    <p class="text-neutral-subtlest sm:font-semibold">Name</p>
                    <p class="text-steel-900">
                      {{ load.broker?.legal_name }}
                    </p>
                  </div>
                  <div
                    v-if="load.broker?.support_email"
                    class="flex items-center justify-between"
                  >
                    <p class="text-neutral-subtlest sm:font-semibold">Email</p>
                    <a
                      :href="`mailto:${load.broker?.support_email}`"
                      class="break-words text-sm font-medium leading-tight text-yellow-700 underline"
                    >
                      {{ load.broker?.support_email }}
                    </a>
                  </div>
                </div>
              </CollapsibleLoadDetails>

              <div
                v-if="isLoadBoard"
                class="flex flex-col gap-4"
                :class="{
                  'pb-24': !isDesktop,
                }"
              >
                <CollapsibleLoadDetails
                  v-if="isLoadBoard && !isDesktop"
                  title="Equipment"
                >
                  <div
                    class="flex flex-col gap-4 p-3 font-normal"
                    :class="{
                      'border-t border-light-gray': !isDesktop,
                    }"
                  >
                    <div
                      class="grid grid-cols-3 text-left"
                      :class="{
                        'gap-2': !isDesktop,
                      }"
                    >
                      <!-- <div
                        class="col-span-1 flex flex-col gap-1"
                        :class="{
                          'col-span-3 !flex-row justify-between': !isDesktop,
                        }"
                      >
                        <p class="text-neutral-subtlest sm:font-semibold">
                          Load Type
                        </p>
                        <p class="text-steel-900">LTL</p>
                      </div> -->

                      <div
                        class="col-span-1 flex flex-col gap-1"
                        :class="{
                          'col-span-3 !flex-row justify-between': !isDesktop,
                        }"
                      >
                        <p class="text-neutral-subtlest sm:font-semibold">
                          Truck Type
                        </p>
                        <p class="text-steel-900">
                          {{
                            formatEquipmentType(load?.posted_equipment_types) ||
                            '-'
                          }}
                        </p>
                      </div>

                      <div
                        v-if="load?.trailer_length"
                        class="col-span-1 flex flex-col gap-1"
                        :class="{
                          'col-span-3 !flex-row justify-between': !isDesktop,
                        }"
                      >
                        <p class="text-neutral-subtlest sm:font-semibold">
                          Length
                        </p>
                        <p class="text-steel-900">
                          {{ trailerLength }}
                        </p>
                      </div>
                    </div>
                  </div>
                </CollapsibleLoadDetails>

                <div v-if="isDesktop">
                  <div
                    class="flex flex-col gap-2 rounded-lg border border-light-gray p-3 text-left"
                  >
                    <div>
                      <img
                        v-if="brokerLogo"
                        class="flex h-8 max-w-48 items-center text-2xs"
                        :src="brokerLogo"
                        alt="broker logo"
                      />

                      <p class="text-steel-800">
                        {{
                          load.broker_name || load.order?.company?.legal_name
                        }}
                      </p>
                    </div>

                    <a
                      v-if="formattedPhone"
                      class="text-xs text-steel-900"
                      :href="`tel:+${load.broker_phone_number}${load.broker_phone_extension ? `,${load.broker_phone_extension}` : ''}`"
                    >
                      {{ formattedPhone }}
                    </a>
                    <p v-else>-</p>

                    <a
                      :href="`mailto:${load.broker_email}?subject=TFX%20Carrier%20Email%20for%20Load%20${load?.external_order_or_load_number}`"
                      class="text-xs text-steel-900"
                      :class="{
                        'underline sm:text-gold': load.broker_email,
                      }"
                    >
                      {{ load.broker_email || '-' }}
                    </a>

                    <div
                      v-if="isConnected"
                      class="flex w-max items-center rounded-md border border-sun-400 py-1 pl-2 pr-3"
                    >
                      <img
                        class="-mb-2 -ml-1 h-10 w-10 text-2xs"
                        src="@/assets/highway-badge-2.svg"
                        alt="badge"
                      />
                      <p class="text-base text-steel-900">You're Connected!</p>
                    </div>

                    <div
                      v-else-if="!is_connections_loading"
                      class="flex rounded-md border border-sun-400 bg-sun-50 pb-2 pl-2 pr-3 pt-1"
                    >
                      <img
                        class="-mb-2 -ml-1 h-10 w-10 text-2xs"
                        src="@/assets/highway-badge-2.svg"
                        alt="badge"
                      />

                      <div class="flex flex-col gap-1 pt-[5px]">
                        <p class="text-base text-steel-900">
                          You're not connected to this broker.
                        </p>

                        <p class="font-normal text-steel-800">
                          To book with this broker, you must be connected first.

                          <a
                            class="font-medium underline"
                            target="_blank"
                            href="https://service.highway.com/en/articles/13062649-why-do-i-need-to-be-connected-to-a-broker-before-booking-a-load"
                          >
                            Why?
                          </a>
                        </p>
                      </div>
                    </div>

                    <div
                      v-if="canViewVettedBrokerBadge"
                      class="flex gap-1 rounded-md border border-teal-600 py-2 pl-2 pr-3"
                    >
                      <img
                        class="mr-1 h-7 w-7 pl-1 text-2xs"
                        src="@/assets/blue-check.svg"
                        alt="checkmark"
                      />

                      <div class="mt-0.5 flex flex-col gap-1">
                        <p class="text-base text-steel-900">Vetted Broker</p>

                        <p class="font-normal text-steel-800">
                          This broker has a history of meeting its payment
                          obligations to Triumph or has demonstrated financial
                          capacity to do so. This information is historical and
                          not a guarantee of future creditworthiness or payment
                          performance.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else>
                  <CollapsibleLoadDetails title="Broker">
                    <div
                      class="flex flex-col p-3 font-normal sm:gap-4"
                      :class="{
                        'border-t border-light-gray': !isDesktop,
                      }"
                    >
                      <div class="grid grid-cols-3 gap-2 text-left">
                        <div
                          class="col-span-3 flex items-center justify-between"
                        >
                          <p class="text-neutral-subtlest">
                            {{
                              load.broker_name ||
                              load.order?.company?.legal_name
                            }}
                          </p>
                          <img
                            v-if="brokerLogo"
                            class="flex h-8 max-w-48 items-center text-2xs"
                            :src="brokerLogo"
                            alt="broker logo"
                          />
                        </div>

                        <div
                          class="col-span-1 col-span-3 flex !flex-row flex-col justify-between gap-1"
                        >
                          <p class="text-steel-900">Connection Status</p>
                          <div class="flex gap-1">
                            <p
                              v-if="isConnected"
                              class="text-steel-900"
                            >
                              You're Connected
                            </p>
                            <p
                              v-else
                              class="text-steel-900"
                            >
                              Not Connected
                            </p>
                          </div>
                        </div>

                        <div
                          class="col-span-3 flex items-center justify-between"
                        >
                          <p class="text-neutral-subtlest">Phone Number</p>
                          <a
                            v-if="formattedPhone"
                            class="text-xs text-steel-900 underline sm:text-gold"
                            :href="`tel:+${load.broker_phone_number}${load.broker_phone_extension ? `,${load.broker_phone_extension}` : ''}`"
                          >
                            {{ formattedPhone }}
                          </a>
                          <p v-else>-</p>
                        </div>

                        <div
                          class="col-span-3 flex items-center justify-between"
                        >
                          <p class="text-neutral-subtlest">Email</p>
                          <a
                            :href="`mailto:${load.broker_email}?subject=TFX%20Carrier%20Email%20for%20Load%20${load?.external_order_or_load_number}`"
                            class="text-xs text-steel-900 underline sm:text-gold"
                          >
                            {{ load.broker_email }}
                          </a>
                        </div>
                      </div>
                    </div>
                  </CollapsibleLoadDetails>
                </div>

                <div
                  v-if="recommendedLoads?.length > 0"
                  class="mb-2 flex w-full flex-col items-start gap-5"
                >
                  <p class="text-lg font-medium">
                    {{
                      isDesktop ? 'Similar Loads' : 'Loads at your destination'
                    }}
                  </p>
                  <div class="flex w-full flex-col items-center gap-6">
                    <LoadDetail
                      v-for="(recommendation, index) in recommendedLoads"
                      :key="index"
                      :load="recommendation"
                      :is-recommended="true"
                      :open-in-new-tab="isDesktop ? false : true"
                      class="max-w-[400px]"
                    ></LoadDetail>

                    <router-link
                      v-if="isDesktop"
                      :to="{
                        name: 'Load Board',
                      }"
                      class="flex items-center justify-center gap-2"
                    >
                      <p class="text-base">See More Loads</p>
                    </router-link>
                  </div>
                </div>

                <TriumphCollapsibleDetail
                  v-if="isLoadBoard"
                  class="mb-8"
                ></TriumphCollapsibleDetail>
              </div>
            </div>
          </div>
          <div
            v-if="isLoadBoard && !is_connections_loading"
            class="mt-2 flex justify-end gap-2"
            :class="{
              'fixed bottom-0 left-0 z-30 w-full bg-white px-4 pb-8 pt-4':
                !isDesktop,
              'sticky bottom-0 left-0 z-30 mt-4 w-full border-t border-light-gray bg-white py-6 pr-6':
                isDesktop,
              'pb-10': showBanner,
            }"
          >
            <div
              class="flex gap-4"
              :class="{
                'w-full flex-col': !isDesktop,
              }"
            >
              <div
                class="flex"
                :class="{
                  'gap-4': isDesktop,
                  'gap-2 [&>*]:flex-1': !isDesktop,
                }"
              >
                <ButtonComponent
                  v-if="displayManageOfferButton"
                  variant="primary"
                  @click="handleEditOffer"
                >
                  Manage Offer
                </ButtonComponent>
                <ButtonComponent
                  class="flex min-w-max justify-center"
                  :class="{
                    'py-4': !isDesktop,
                    'flex-1': isConnected && supportsPlaceOfferOrBookNowModal,
                    'w-full': !(
                      isConnected && supportsPlaceOfferOrBookNowModal
                    ),
                  }"
                  @click="openContactBrokerBottomSheet"
                >
                  Contact Broker
                </ButtonComponent>
                <ButtonComponent
                  v-if="displayBookNowButton"
                  variant="secondary"
                  class="flex min-w-max flex-1 justify-center"
                  :class="{
                    'py-4': !isDesktop,
                  }"
                  @click="handleBookNow"
                >
                  {{ modalText }}
                </ButtonComponent>
              </div>
              <ButtonComponent
                v-if="!isConnected"
                class="!min-w-[90px]"
                variant="primary"
                :disabled="is_connect_button_loading"
                :is-loading="is_connect_button_loading"
                @click="handleConnect"
              >
                Connect
              </ButtonComponent>
            </div>
          </div>
        </div>
      </div>

      <LoadDetailMap
        v-if="!isLoading && isDesktop"
        :is-desktop="isDesktop"
        :load="load"
      ></LoadDetailMap>
    </div>

    <TeleportModalWrapperComponent
      :margin-top="innerHeight < 700 ? 'mt-[60px]' : ''"
      :align="innerHeight < 700 ? 'start' : 'center'"
      :is-modal-open="showAssignVehicleModal"
    >
      <CarrierWorkflowModalComponent
        :load-id="load?.id"
        :external-load-id="load?.shipment_identifier"
        :load-ticket-id="selectedLoadTicketId || carrierVisibleTicketId || ''"
        :is-load-trackable="
          load?.billable_miles ? load.billable_miles > 150 : true
        "
        :origin="
          load?.stops?.length ? stopDetails(load.stops[0]) : { city: '-' }
        "
        :destination="
          load?.stops?.length > 1
            ? stopDetails(load.stops[load.stops.length - 1])
            : { city: '-' }
        "
        :assigned-vehicle="assignedVehicle"
        :pending-vehicle="pendingVehicle"
        @update-assigned-vehicle="updateAssignedVehicle"
        @close-modal="showAssignVehicleModal = false"
        @open-view-equipment-modal="
          () => {
            showAssignVehicleModal = false;
            showEquipmentModal = true;
          }
        "
      />
    </TeleportModalWrapperComponent>
    <Teleport to="body">
      <DisplayEquipmentTableModal
        :visible="showEquipmentModal"
        :show-dismiss-button-prop="true"
        @close="
          () => {
            showEquipmentModal = false;
            showAssignVehicleModal = true;
          }
        "
      />
    </Teleport>

    <BookNowBottomSheet
      v-if="carrierSubmitOfferUpdateEnabled"
      :visible="showBookNowBottomSheet"
      :load-id="load?.id"
      @close="closeBookNowBottomSheet"
      @withdraw="handleWithdraw"
    ></BookNowBottomSheet>
    <BookNowBottomSheetLegacy
      v-else
      :visible="showBookNowBottomSheet"
      :load-id="load?.id"
      @close="closeBookNowBottomSheet"
      @withdraw="handleWithdraw"
    ></BookNowBottomSheetLegacy>

    <WithdrawOfferBottomSheet
      :visible="showWithDrawBottomSheet"
      @cancel="cancelDrawBottomSheet"
      @close="toggleWithDrawBottomSheet"
    ></WithdrawOfferBottomSheet>

    <ContactBrokerBottomSheet
      :visible="showContactBrokerBottomSheet"
      :contact-information="brokerContactInformation"
      @close="closeContactBrokerBottomSheet"
    ></ContactBrokerBottomSheet>
  </banners>
</template>

<script setup>
import { ref, computed, onBeforeMount, onBeforeUnmount, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toast-notification';
import dayjs from 'dayjs';
import Popper from 'vue3-popper';
import {
  Banners,
  ButtonComponent,
  CarrierWorkflowModalComponent,
  TeleportModalWrapperComponent,
  displayEquipmentTableModal as DisplayEquipmentTableModal,
  BookNowBottomSheet,
  BookNowBottomSheetLegacy,
  CollapsibleLoadDetails,
  LoadDetail,
  LoadDetailMap,
  TriumphCollapsibleDetail,
  ContactBrokerBottomSheet,
  WithdrawOfferBottomSheet,
} from '@/components';
import {
  formatPrice,
  getAppointmentDate,
  getStopDay,
  getStopTime,
  formattedStreetAddress,
  formattedCityStateAddress,
  formatLoadStatus,
  formatEquipmentType,
  capitalizeWords,
  formatStopWindow,
} from '@/utils';
import { useScreenSize } from '@/composables';
import { formatPhoneParenthesis, fromNowFirstUnit } from '@/common';

const { innerHeight } = useScreenSize();
const showAssignVehicleModal = ref(false);

const showEquipmentModal = ref(false);
const store = useStore();
const router = useRouter();
const toast = useToast();
const { screenSize } = useScreenSize();

const isLoading = ref(false);

const canViewLoads = computed(() =>
  store.state.users?.currentUser?.scopes?.includes('carrier_view_posted_loads')
);
const isLoadBoard = ref(false);

const isDesktop = computed(() => screenSize.value === 'desktop');

const carrierSubmitOfferUpdateEnabled = computed(
  () => store.state.users.featureFlags?.carrier_submit_offer_update?.enabled
);

const formattedTime = (stop) => {
  const { time, day } = getAppointmentDate(stop);

  return `${day}, ${time}`;
};

const wasLoadCreatedAfterStopAppt = (stop) => {
  if (stop.scheduled_window_ended_at_in_stop_timezone) {
    return (
      Date.parse(stop.created_at) >
      Date.parse(stop.scheduled_window_ended_at_in_stop_timezone)
    );
  }
  return false;
};

const hasIncompleteStopAddress = (stop) => {
  if (stop.street1?.length) {
    return false;
  }
  return true;
};

const stopStatus = (stops, index) => {
  const stop = stops[index];

  if (stop.departed_at_in_stop_timezone) {
    return { status: 'Departed', color: 'green' };
  } else if (stop.arrived_at_in_stop_timezone) {
    return { status: 'Arrived', color: 'green' };
  } else if (wasLoadCreatedAfterStopAppt(stop)) {
    return {
      // status: 'Unknown Stop Status: Load Data Received Late',
      status: 'Unknown Stop Status',
      color: 'gray',
    };
  } else if (hasIncompleteStopAddress(stop)) {
    return {
      // status: 'Unknown Stop Status: No Street Address Provided',
      status: 'Unknown Stop Status',
      color: 'gray',
    };
  } else if (
    (index === 0 &&
      !stops[index + 1]?.arrived_at_in_stop_timezone &&
      !stops[index + 1]?.departed_at_in_stop_timezone) ||
    (index !== 0 &&
      (stops[index - 1]?.departed_at_in_stop_timezone ||
        wasLoadCreatedAfterStopAppt(stops[index - 1])))
  ) {
    return { status: 'En Route', color: 'light-blue' };
  } else {
    return { status: 'Not Started', color: 'gray' };
  }
};

const showBookNowBottomSheet = ref(false);
const openBookNowBottomSheet = () => {
  showBookNowBottomSheet.value = true;
};
const closeBookNowBottomSheet = () => {
  showBookNowBottomSheet.value = false;
};

const showWithDrawBottomSheet = ref(false);
const toggleWithDrawBottomSheet = () => {
  showWithDrawBottomSheet.value = !showWithDrawBottomSheet.value;
};
const handleWithdraw = () => {
  closeBookNowBottomSheet();
  toggleWithDrawBottomSheet();
};
const cancelDrawBottomSheet = () => {
  openBookNowBottomSheet();
  toggleWithDrawBottomSheet();
};

const showContactBrokerBottomSheet = ref(false);
const openContactBrokerBottomSheet = async () => {
  showContactBrokerBottomSheet.value = true;

  await store.dispatch('activities/postActivity', {
    user_activity: {
      is_type: 'contact_broker_click',
      action_id: load?.value?.id,
    },
  });
};
const closeContactBrokerBottomSheet = () => {
  showContactBrokerBottomSheet.value = false;
};

const brokerContactInformation = computed(() => ({
  name: load?.value?.broker_name,
  phone_number: load?.value?.broker_phone_number,
  phone_extension: load?.value?.broker_phone_extension || '',
  email: load?.value?.broker_email,
}));

const backUrl = computed(() => {
  if (isLoadBoard?.value) {
    return router.options.history.state.back || '/carrier/load-board';
  } else {
    return router.options.history.state.back || '/carrier/loads';
  }
});

const canViewCarrierRecommendations = computed(
  () => store.state.users.featureFlags?.fe_carrier_similar_loads.enabled
);

const canViewVettedBrokerBadge = computed(
  () =>
    store.state.users.featureFlags?.fe_vetted_broker_badge?.enabled &&
    load?.value?.is_qualified_by_triumph_for_load_board
);

onBeforeMount(async () => {
  isLoading.value = true;

  const loadId = parseInt(router?.currentRoute?.value?.params?.id);

  let res;
  let navigateBack = false;
  let navigateRouteName = 'My Loads';
  let errorMessage;
  if (canViewLoads?.value) {
    const isPreLoaded = store.state.loads?.preloaded_load_response?.id;

    let loadBoardRes;
    let loadDetailRes;
    if (isPreLoaded) {
      isLoading.value = false;
      isLoadBoard.value = true;

      // Can assume load board details, is coming from TFX
      const [loadBoardDetailRes] = await Promise.all([
        store.dispatch('loads/getLoadBoardDetail', {
          id: loadId,
          quiet: !!isPreLoaded,
        }),
        store.dispatch('brokers/getBrokers', {}),
        store.dispatch('loads/getOfferByLoadId', { load_id: loadId }),
      ]);

      loadBoardRes = loadBoardDetailRes;
    } else {
      // If has canViewLoads scope
      // check if load is a posted load, otherwise grab load detail
      const [loadBoardDetailRes, loadDetailResponse] = await Promise.all([
        store.dispatch('loads/getLoadBoardDetail', {
          id: loadId,
          quiet: !!isPreLoaded,
        }),
        store.dispatch('loads/getLoadDetail', {
          id: loadId,
          query: 'carrier_show',
          quiet: !!isPreLoaded,
        }),
        store.dispatch('brokers/getBrokers', {}),
        store.dispatch('loads/getOfferByLoadId', { load_id: loadId }),
      ]);

      loadBoardRes = loadBoardDetailRes;
      loadDetailRes = loadDetailResponse;
    }

    if (loadBoardRes?.data) {
      isLoadBoard.value = true;
      res = loadBoardRes;

      if (!loadBoardRes?.data?.id && loadDetailRes?.data?.id) {
        isLoadBoard.value = false;
        res = loadDetailRes;
      }

      const userNotAuthorized =
        ([404, 400, 403, 429].includes(loadBoardRes?.status) ||
          !loadBoardRes?.data?.can_be_posted) &&
        !loadDetailRes?.data;

      if (userNotAuthorized) {
        navigateBack = true;
        navigateRouteName = 'Load Board';

        if (res?.status === 403) {
          errorMessage = 'Unauthorized access';
        } else if (res?.data?.error?.message) {
          errorMessage = res?.data?.error?.message;
        }
      }
    } else {
      res = loadDetailRes;

      if ([404, 400].includes(loadDetailRes?.status)) {
        navigateBack = true;
      }
    }
  } else {
    const [loadDetailRes] = await Promise.all([
      store.dispatch('loads/getLoadDetail', {
        id: loadId,
        query: 'carrier_show',
      }),
    ]);

    res = loadDetailRes;

    if ([404, 400].includes(loadDetailRes?.status)) {
      navigateBack = true;
    }
  }

  if (navigateBack) {
    // load detail not found or load not posted
    // route to load board

    const message =
      res?.status === 429
        ? 'Maximum daily search limit exceeded'
        : errorMessage
          ? errorMessage
          : 'Invalid Load Posting';
    toast.open({
      message,
      type: 'error',
      duration: 5000,
      dismissible: true,
      position: 'top-right',
    });

    router.push({
      name: navigateRouteName,
    });
  }

  const company_id = res?.data?.broker?.id || res?.data?.broker_company_id;

  await store.commit('loads/setLoadDetail', {
    ...res.data,
    stops: res?.data?.stops?.map((stop) => ({
      ...stop,
      latitude_for_map: stop?.lat || stop?.latitude_for_map,
      longitude_for_map: stop?.lon || stop?.longitude_for_map,
    })),
  });
  await store.dispatch('brokers/getCompanyById', company_id);

  isLoading.value = false;

  if (canViewCarrierRecommendations.value) {
    store.dispatch('loads/getRecommendedLoads', {
      type: 'similar_loads',
      load_id: loadId,
      per_page: 4,
    });
  }

  if (!isDesktop.value) {
    store.dispatch('loads/getRecommendedLoads', {
      type: 'loads_at_destination',
      load_id: loadId,
    });
  }
});

const showBanner = computed(
  () =>
    !store.state.errors.forceHideErrorBanner &&
    store.state.errors.showErrorBanner
);

onBeforeUnmount(() => {
  if (isLoadBoard?.value) {
    store.commit('loads/setLoadDetail', {});
    store.commit('loads/setPreLoadResponse', {});
    store.commit('loads/setOffer', {});
    store.commit('loads/setSimilarLoads', []);
    store.commit('loads/setLoadsAtDestination', []);
  }
});

const load = computed(() => store.state.loads.load_details);
const placed_offer = computed(() => store.state.loads?.offer);
const recommendedLoads = computed(() => {
  if (isDesktop.value) {
    return store.state.loads?.similar_loads?.loads?.slice(0, 4);
  } else {
    return store.state.loads?.loads_at_destination?.loads?.slice(0, 4);
  }
});

const offer_status = computed(() => {
  if (placed_offer?.value?.status === 'accepted') {
    return 'Offer Accepted';
  } else if (placed_offer?.value?.status === 'booked') {
    return 'Load Booked';
  } else {
    return 'Offer Placed';
  }
});

const offer_text = computed(() => {
  if (placed_offer?.value?.status === 'accepted') {
    return `Your offer on this load was accepted on ${dayjs(placed_offer?.value?.created_at).format('MM/DD/YYYY')} for ${formatPrice(placed_offer?.value?.rate, 0)}.`;
  } else if (placed_offer?.value?.status === 'booked') {
    return `You booked this load on ${dayjs(placed_offer?.value?.created_at).format('MM/DD/YYYY')} for ${formatPrice(placed_offer?.value?.rate, 0)}.`;
  } else if (
    placed_offer?.value?.negotiation?.current_offer_rate?.direction === 'broker'
  ) {
    return `The broker has submitted a counter offer on this load on ${dayjs(placed_offer?.value?.created_at).format('MM/DD/YYYY')} for ${formatPrice(placed_offer?.value?.rate, 0)}.`;
  } else {
    return `You placed an offer on this load on ${dayjs(placed_offer?.value?.created_at).format('MM/DD/YYYY')} for ${formatPrice(placed_offer?.value?.rate, 0)}.`;
  }
});

const manage_offer_enabled = computed(
  () => store.state.users.featureFlags?.edit_book_now?.enabled
);

const displayManageOfferButton = computed(() => {
  return (
    manage_offer_enabled.value &&
    placed_offer?.value?.id &&
    placed_offer?.value?.status === 'pending' &&
    placed_offer?.value?.negotiation?.current_offer_rate?.direction !== 'broker'
  );
});

const offer_loading = computed(
  () => store.state.loads?.is_submit_offer_loading
);

const handleEditOffer = () => {
  openBookNowBottomSheet();
};

const handleWithdrawOffer = async () => {
  const res = await store.dispatch('loads/withdrawOffer', {
    id: placed_offer?.value?.id,
  });

  if ([200, 201, 204].includes(res.status)) {
    await toast.open({
      message: 'Offer withdrawn',
      position: 'top-right',
      duration: 5000,
    });

    await store.dispatch('loads/getOfferByLoadId', {
      load_id: store.state.loads?.load_details?.id,
    });
  } else {
    await toast.open({
      message: 'Unexpected error. Please try again.',
      type: 'error',
      position: 'top-right',
      duration: 5000,
    });
  }
};

const assignedVehicle = ref({
  id: '',
  truck_number: '',
  vin: '',
});

const pendingVehicle = ref({
  id: '',
  truck_number: '',
  vin: '',
});

watch(
  load,
  (newVal) => {
    if (newVal?.carrier_vehicle) {
      assignedVehicle.value = newVal.carrier_vehicle;
    }
    if (newVal?.pending_driver_carrier_vehicle) {
      pendingVehicle.value = newVal.pending_driver_carrier_vehicle;
    }
  },
  { immediate: true }
);

const wasAssignedVehicleUpdated = ref(false);

const updateAssignedVehicle = (vehicle) => {
  wasAssignedVehicleUpdated.value = true;
  assignedVehicle.value = {
    id: vehicle?.id || '',
    truck_number: vehicle?.truck_number || '',
    vin: vehicle?.vin || '',
  };
};

const loadDetailsLoading = computed(
  () => store.state.loads.isLoadDetailsLoading
);

const broker_connections = computed(() => store.state.brokers.connections);

const load_connection = computed(() =>
  broker_connections?.value?.find(
    (connection) => connection?.company_id === load?.value?.broker_company_id
  )
);

const isConnected = computed(
  () => load_connection?.value?.status === 'onboarded'
);

const stopDetails = (stop) => {
  if (!stop) {
    return false;
  }
  return {
    id: stop.id,
    street1: capitalizeWords(stop.street1),
    street2: capitalizeWords(stop.street2),
    city: capitalizeWords(stop.city) || stop.area_five_zip_city || '',
    state: stop.state || stop.area_five_zip_state || '',
    zip: stop.postal_code || '',
    appointmentDay: getStopDay(
      stop.scheduled_window_started_at_in_stop_timezone ||
        stop.scheduled_window_ended_at_in_stop_timezone,
      stop.timezone,
      { monthNumeric: true, yearDigits: 2, includesYear: false }
    ),
    appointmentTime: getStopTime(
      stop.scheduled_window_started_at_in_stop_timezone ||
        stop.scheduled_window_ended_at_in_stop_timezone,
      stop.timezone
    ),
    stopDateRaw:
      stop.scheduled_window_started_at_in_stop_timezone ||
      stop.scheduled_window_ended_at_in_stop_timezone,
    timezone: stop.timezone,
    arrivalDay: getStopDay(stop.arrived_at_in_stop_timezone, stop.timezone),
    arrivalTime: getStopTime(stop.arrived_at_in_stop_timezone, stop.timezone),
    departureDay: getStopDay(stop.departed_at_in_stop_timezone, stop.timezone),
    departureTime: getStopTime(
      stop.departed_at_in_stop_timezone,
      stop.timezone
    ),
  };
};

const lane_prediction = computed(() => load?.value?.lane_prediction);

const quickPayRate = computed(
  () => load?.value?.triumph_financing_terms?.quick_pay?.rate
);

const broker = computed(() => store.state.brokers?.company);
const brokerLogo = computed(() => load?.value?.broker_logo?.url);

const billableMiles = computed(() => {
  const totalMiles = load?.value?.posted_miles;

  if (!totalMiles) {
    return null;
  }

  return (Math.round(totalMiles * 100) / 100)?.toLocaleString();
});

const limitCharacter = (string) => {
  if (!string) return null;

  if (string?.length >= 500) {
    return `${string?.slice(0, 500)}...`;
  }

  return string;
};

const loadComments = computed(() => limitCharacter(load?.value?.comments));

const formattedPhone = computed(() => {
  return formatPhoneParenthesis(
    load?.value?.broker_phone_number,
    load?.value?.broker_phone_extension
  );
});

const numberOfStops = computed(() => {
  if (load?.value?.number_of_stops) {
    if (load?.value?.number_of_stops === 1) {
      return '1 Stop';
    } else {
      return `${load?.value?.number_of_stops} Stops`;
    }
  } else if (load?.value?.stops?.length === 0) {
    return '';
  } else if (load?.value?.stops?.length === 1) {
    return '1 Stop';
  } else {
    return `${load?.value?.stops?.length} Stops`;
  }
});

const linkOutUrl = computed(() => load?.value?.external_book_it_now_url);
const bookItNowType = computed(() => load?.value?.book_it_now_type);

const supportEitherBookNowOrPlaceOffer = computed(() => {
  return (
    bookItNowType.value === 'book_it_now_only' ||
    bookItNowType.value === 'offers_only' ||
    bookItNowType.value === 'offers_and_book_it_now' ||
    bookItNowType.value === 'offers_only_static' ||
    bookItNowType.value === 'offers_and_book_it_now_static'
  );
});

const supportsPlaceOfferOrBookNowModal = computed(() => {
  if (!load?.value?.price) {
    return (
      bookItNowType.value === 'offers_only' ||
      bookItNowType.value === 'offers_and_book_it_now' ||
      bookItNowType.value === 'redirect_uri_link_out' ||
      bookItNowType.value === 'offers_only_static' ||
      bookItNowType.value === 'offers_and_book_it_now_static'
    );
  }

  if (bookItNowType.value === 'redirect_uri_link_out') {
    return true;
  }

  return supportEitherBookNowOrPlaceOffer?.value;
});

const modalText = computed(() => {
  if (bookItNowType.value === 'redirect_uri_link_out') {
    return 'Continue';
  }
  if (bookItNowType.value === 'book_it_now_only') {
    return 'Book Now';
  }

  if (bookItNowType.value === 'offers_only') {
    return 'Place Offer';
  }

  if (
    bookItNowType.value === 'offers_and_book_it_now' ||
    bookItNowType.value === 'offers_and_book_it_now_static'
  ) {
    return 'Book Now or Place Offer';
  }

  return 'Place Offer';
});

const carrier_vehicles = computed(
  () => store.state.loads?.eld_carrier_vehicles
);

const displayBookNowButton = computed(() => {
  const hasEldVehicles =
    bookItNowType.value === 'redirect_uri_link_out' ||
    carrier_vehicles?.value?.length > 0;

  const isEligible =
    isConnected.value &&
    supportsPlaceOfferOrBookNowModal.value &&
    hasEldVehicles;

  const offerStatus = placed_offer?.value?.status;
  const noOfferOrInvalid =
    !placed_offer.value ||
    offerStatus === 'rejected' ||
    offerStatus === 'expired';

  return isEligible && noOfferOrInvalid;
});

const is_connections_loading = computed(
  () => store.state.brokers.is_connections_loading
);

const handleBookNow = async () => {
  if (bookItNowType.value === 'redirect_uri_link_out' && linkOutUrl.value) {
    // open the window immediately outside the try/catch
    const newWindow = window.open('', '_blank');

    try {
      await store.dispatch('activities/postActivity', {
        user_activity: {
          is_type: 'redirect_uri_link_out',
          action_id: load?.value?.id,
          params: {
            carrier_id: broker?.value?.carrier_id,
            load_id: load?.value?.id,
            rate: load?.value?.price,
            redirect_uri: linkOutUrl.value,
          },
        },
      });

      // update the already-opened window
      if (newWindow) {
        newWindow.location.href = linkOutUrl.value;
      }
    } catch (error) {
      console.log('error', error);
    }
  } else {
    openBookNowBottomSheet();
  }

  if (
    modalText.value === 'Place Offer' ||
    modalText.value === 'Book Now or Place Offer'
  ) {
    await store.dispatch('activities/postActivity', {
      user_activity: {
        is_type: 'place_offer_click',
        action_id: load?.value?.id,
      },
    });
  }
};

const is_connect_button_loading = computed(
  () => store.state.loads?.is_carrier_connect_button_loading
);

const handleConnect = async () => {
  console.log('Connect redirect');
  try {
    const response = await store.dispatch(
      'loads/sendCarrierToOnboarding',
      load?.value?.id
    );

    if (
      response?.response?.status === 422 &&
      response?.response?.data?.error === 'broker_onboarding_error'
    ) {
      toast.open({
        message:
          'Sorry, the broker you would like to connect with cannot currently onboard any new carriers',
        type: 'error',
        duration: 8000,
        dismissible: true,
        position: 'top-right',
      });
    } else {
      // Send user to onboarding
      window.location.href = `/monitor/api/v1/loads/${load?.value?.id}/send_carrier_to_onboarding`;
    }
  } catch (error) {
    console.log('Error: ', error);
  }
};

const ratePerMile = computed(() => {
  return formatPrice(load?.value?.rate_per_mile);
});

const loadRate = computed(() => {
  return lane_prediction?.value?.posted_rate_assessment;
});

const loadRateClass = computed(() => {
  switch (loadRate?.value) {
    case 'Great':
      return 'bg-aqua-700 text-white';
    case 'Good':
      return 'bg-aqua-500 text-white';
    default:
      return '';
  }
});

const loadRateText = computed(() => {
  switch (loadRate?.value) {
    case 'Great':
      return 'Great Deal';
    case 'Good':
      return 'Good Deal';
    default:
      return null;
  }
});

const weightUnit = {
  pound: 'lbs',
  kilogram: 'kg',
};

const loadWeight = computed(() => {
  if (!load?.value?.weight || load?.value?.weight === 0) {
    return '-';
  }

  return `${load?.value?.weight?.toLocaleString('en-US')} ${weightUnit[load?.value?.weight_unit]}`;
});

const MAX_COMMODITY_LENGTH = 40;

const loadCommodity = computed(() => {
  const commodity = load?.value?.posted_commodity_description;
  if (!commodity) return null;
  return commodity.length > MAX_COMMODITY_LENGTH
    ? commodity.slice(0, MAX_COMMODITY_LENGTH) + '...'
    : commodity;
});

const canShowLoadComments = computed(
  () => loadCommodity.value && loadCommodity.value.length > 0
);

const lengthUnit = {
  inch: '"',
  foot: "'",
  meter: 'm',
};

const trailerLength = computed(
  () =>
    `${load?.value?.trailer_length}${load?.value?.trailer_length_unit ? lengthUnit[load?.value?.trailer_length_unit] : `'`}`
);

const targetRatePercentage = computed(() => {
  const { low_buy_rate, high_buy_rate } = lane_prediction?.value || {};

  if (high_buy_rate === low_buy_rate) return 0; // avoid division by zero
  const percentage =
    ((load?.value?.rate_per_mile - low_buy_rate) /
      (high_buy_rate - low_buy_rate)) *
    100;
  const clamp = Math.max(0, Math.min(100, percentage)); // clamp between 0 and 100
  return clamp;
});

const isBelowMarket = computed(() => {
  const { low_buy_rate, target_buy_rate } = lane_prediction?.value || {};

  if (target_buy_rate < low_buy_rate) {
    return true;
  }

  return false;
});

const isAboveMarket = computed(() => {
  const { high_buy_rate, target_buy_rate } = lane_prediction?.value || {};

  if (target_buy_rate > high_buy_rate) {
    return true;
  }

  return false;
});

const selectedLoadTicketId = computed(() => load.value.tickets?.[0]?.id);

const carrierVisibleTicketId = computed(
  () => load.value.carrier_visible_tickets?.[0]?.id
);
</script>
